import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ErrorTrackingService } from './error-tracking.service';
import { normalizeEndpoint } from './normalize-endpoint';
import { isPageUnloading, watchPageUnload } from './page-unload';

export type HttpStatusClass = 'network' | 'timeout' | '4xx' | '5xx' | 'other';

export const REPORT_THROTTLE_MS = 30_000;
const AUTOMATION_AUDIENCE_PATH = /\/automations\/campaigns\/([^/]+)\/customers/;

export function httpStatusClass(status: number): HttpStatusClass {
  if (status === 0) {
    return 'network';
  }
  if (status === 408 || status === 504) {
    return 'timeout';
  }
  if (status >= 500 && status < 600) {
    return '5xx';
  }
  if (status >= 400 && status < 500) {
    return '4xx';
  }
  return 'other';
}

/**
 * Reports every failed HttpClient request to Bugsnag, grouped by method + endpoint + status class,
 * so each failing endpoint is one Bugsnag error whose timeline shows when it started failing.
 * Registered after `TraceIdInterceptor` so the Datadog trace ID is on the request.
 */
@Injectable()
export class ErrorTrackingInterceptor implements HttpInterceptor {
  private readonly lastReportedAt = new Map<string, number>();

  constructor(private readonly errorTracking: ErrorTrackingService) {
    watchPageUnload();
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const startedAt = performance.now();
    return next.handle(request).pipe(
      tap({
        error: (error: unknown) => {
          if (error instanceof HttpErrorResponse) {
            this.report(request, error, Math.round(performance.now() - startedAt));
          }
        }
      })
    );
  }

  private report(request: HttpRequest<any>, error: HttpErrorResponse, durationMs: number): void {
    // 401s are the auth redirect flow, not a failure to act on.
    if (error.status === 401) {
      return;
    }
    // 400s are request validation (e.g. a send time already in the past): user input, not an app failure.
    if (error.status === 400) {
      return;
    }
    // Requests the browser cancelled because the page is reloading or navigating away.
    if (error.status === 0 && isPageUnloading()) {
      return;
    }

    const method = request.method;
    const { host, endpoint } = normalizeEndpoint(error.url ?? request.url);
    const statusClass = httpStatusClass(error.status);
    const groupingHash = `${method} ${host}${endpoint} ${statusClass}`;

    const now = Date.now();
    const last = this.lastReportedAt.get(groupingHash);
    if (last !== undefined && now - last < REPORT_THROTTLE_MS) {
      return;
    }
    this.lastReportedAt.set(groupingHash, now);

    const online = globalThis.navigator?.onLine ?? true;
    const severity = statusClass === '5xx' || statusClass === 'timeout' || (statusClass === 'network' && online)
      ? 'error'
      : 'warning';
    const templateId = AUTOMATION_AUDIENCE_PATH.exec(endpoint)?.[1];

    this.errorTracking.notify({
      errorClass: error.status === 0 ? 'NetworkError' : `HttpError ${error.status}`,
      errorMessage: `${method} ${endpoint} failed with ${error.status === 0 ? 'no response' : error.status}`
    }, (event) => {
      event.severity = severity;
      event.groupingHash = groupingHash;
      event.context = `${method} ${endpoint}`;
      event.addMetadata('http', {
        host,
        method,
        endpoint,
        status: error.status,
        statusClass,
        durationMs,
        online,
        datadogTraceId: request.headers.get('x-datadog-trace-id') ?? undefined
      });
      if (templateId) {
        event.addMetadata('automationAudience', { templateId });
      }
    });
  }
}
